/**
 * Main CTA (lower buy box) behavior: media carousel + thumbnails, variant
 * radio sync and the B2B add-to-cart path.
 *
 * Boots once per `[data-rtnu-main-cta]` section root. The section passes its
 * ids through data attributes:
 *   data-section-id      Shopify section id (carousel/thumbnail node ids)
 *   data-form-id         product form id
 *   data-currency-symbol currency symbol for per-stick pricing
 *   data-sale-enabled    present when theme sale pricing is on
 *   data-b2b             present when the customer is B2B
 *
 * This section does not share assets/rtnu-pdp-hero.js: its carousel ids are
 * `pdp-main-carousel2-*` / `pdp-main-thumbnails2-*`, its pagination dots use
 * `.is-active`, its thumbnails scroll drag-free and a variant change also
 * rewrites the B2B submit-button price.
 *
 * The buy box itself lives in assets/rtnu-buybox.js.
 */
(() => {
  if (window.__rtnuMainCtaBound) return;
  window.__rtnuMainCtaBound = true;

  const MAIN_OPTIONS = { align: 'start', containScroll: 'trimSnaps', slidesToScroll: 1 };
  const THUMB_OPTIONS = { align: 'start', containScroll: 'trimSnaps', dragFree: true };
  const isDesktop = () => window.innerWidth > 768;

  const initCarousel = (root, sectionId) => {
    const mainNode = root.querySelector(`#pdp-main-carousel2-${sectionId}`);
    const thumbsNode = root.querySelector(`#pdp-main-thumbnails2-${sectionId}`);
    const viewport = mainNode?.querySelector('.embla__viewport');
    if (!mainNode || !viewport || typeof EmblaCarousel !== 'function') return;

    if (typeof mainNode._emblaCleanup === 'function') mainNode._emblaCleanup();

    const dots = mainNode.querySelector('.embla__dots');
    let main = EmblaCarousel(viewport, MAIN_OPTIONS);
    mainNode._emblaApi = main;

    let thumbs = null;
    const thumbHandlers = [];

    const destroyThumbs = () => {
      thumbHandlers.forEach(({ slide, handler }) => slide.removeEventListener('click', handler));
      thumbHandlers.length = 0;
      if (thumbs) {
        thumbs.destroy();
        thumbs = null;
      }
      thumbsNode?.querySelectorAll('.embla__slide').forEach((slide) => slide.classList.remove('is-selected'));
    };

    const mountThumbs = () => {
      if (!main || !thumbsNode || !isDesktop()) {
        destroyThumbs();
        return;
      }
      if (thumbs) return;
      const thumbsViewport = thumbsNode.querySelector('.embla__viewport');
      if (!thumbsViewport) return;

      thumbs = EmblaCarousel(thumbsViewport, THUMB_OPTIONS);
      thumbsNode.querySelectorAll('.embla__slide').forEach((slide, index) => {
        const handler = () => main?.scrollTo(index);
        slide.addEventListener('click', handler);
        thumbHandlers.push({ slide, handler });
      });
    };

    const rebuildDots = () => {
      if (!dots || !main) return;
      dots.replaceChildren(
        ...main.scrollSnapList().map((_, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'embla__dot';
          dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
          dot.addEventListener('click', () => main?.scrollTo(index));
          return dot;
        })
      );
    };

    const updateDots = () => {
      if (!dots || !main) return;
      const selected = main.selectedScrollSnap();
      dots.querySelectorAll('.embla__dot').forEach((dot, index) => dot.classList.toggle('is-active', index === selected));
    };

    const updateThumbs = () => {
      if (!main || !thumbsNode) return;
      const selected = main.selectedScrollSnap();
      thumbsNode.querySelectorAll('.embla__slide').forEach((slide, index) => slide.classList.toggle('is-selected', index === selected));
      thumbs?.scrollTo(selected);
    };

    main.on('select', () => {
      updateDots();
      updateThumbs();
    });
    main.on('reInit', () => {
      rebuildDots();
      updateDots();
      updateThumbs();
    });

    rebuildDots();
    mountThumbs();
    updateDots();
    updateThumbs();

    let resizeTimer;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (!main) return;
        main.reInit();
        mountThumbs();
        updateDots();
        updateThumbs();
      }, 150);
    };
    window.addEventListener('resize', onResize);

    mainNode._emblaCleanup = () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', onResize);
      destroyThumbs();
      if (main) {
        main.destroy();
        main = null;
      }
      delete mainNode._emblaApi;
    };
  };

  const initVariants = (root, form, sectionId, currencySymbol, saleEnabled) => {
    const productId = form.querySelector('[name="id"]');
    const mainNode = root.querySelector(`#pdp-main-carousel2-${sectionId}`);
    const slides = root.querySelectorAll(`#pdp-main-carousel2-${sectionId} .embla__slide`);

    form.querySelectorAll('.rtnu-product__variants .rtnu-option').forEach((option) => {
      const radio = option.querySelector('input[type="radio"]');
      const singlePrice = option.querySelector('.rtnu-product__form-item-single-cost-price');
      if (!radio) return;

      // Per-stick price for pouch products.
      const { priceRaw, itemCount } = radio.dataset;
      if (singlePrice && itemCount) {
        singlePrice.textContent = `${currencySymbol}${(priceRaw / itemCount).toFixed(2)}`;
      }

      option.addEventListener('change', (event) => {
        const { value: variantId, dataset } = event.target;
        if (productId) productId.value = variantId;

        const submitPrice = form.querySelector('.rtnu-product__form-submit-price');
        if (submitPrice) {
          submitPrice.innerHTML = `${saleEnabled ? dataset.originalPrice : dataset.price} &bull; Add to Cart`;
        }

        slides.forEach((slide, index) => {
          if (slide.dataset.variant !== undefined && slide.dataset.variant == variantId) {
            mainNode?._emblaApi?.scrollTo(index);
          }
        });
      });
    });
  };

  const initB2bAddToCart = (root, form) => {
    const button = form.querySelector('.rtnu-product__form-submit');
    if (!button) return;

    button.addEventListener('click', async (event) => {
      event.preventDefault();
      const productId = form.querySelector('[name="id"]');
      const qtyInput = form.querySelector('input[name="quantity"]');
      if (!productId || !qtyInput) return;

      const notice = root.querySelector('.rtnu-product__form-notice');
      const price = form.querySelector('.rtnu-product__form-submit-price');
      const status = form.querySelector('.rtnu-product__form-submit-status');

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: productId.value, quantity: qtyInput.value }],
            sections: 'cart-count',
          }),
        });
        const data = await response.json();

        if (data.status === 422) {
          if (notice) {
            Object.assign(notice.style, {
              marginTop: '16px',
              marginBottom: '24px',
              fontSize: '15px',
              color: '#994E0E',
              textAlign: 'center',
              fontWeight: '600',
            });
            notice.textContent = "You've reached the maximum quantity allowed for this product.";
          }
          return;
        }

        if (price) price.style.opacity = '0';
        if (status) status.style.opacity = '1';
        setTimeout(() => {
          if (price) price.style.opacity = '1';
          if (status) status.style.opacity = '0';
        }, 2000);

        document.querySelectorAll('.rtnu-header__cart-count').forEach((count) => {
          count.outerHTML = data.sections['cart-count'];
        });
      } catch (error) {
        console.error('[rtnu-main-cta] B2B add to cart failed', error);
      }
    });
  };

  const initSection = (root) => {
    if (root.dataset.rtnuMainCtaReady) return;
    root.dataset.rtnuMainCtaReady = 'true';

    const { sectionId, formId, currencySymbol = '$' } = root.dataset;
    const saleEnabled = root.hasAttribute('data-sale-enabled');
    const form = root.querySelector(`#${CSS.escape(formId)}`);

    initCarousel(root, sectionId);
    if (form) {
      if (root.hasAttribute('data-b2b')) initB2bAddToCart(root, form);
      initVariants(root, form, sectionId, currencySymbol, saleEnabled);
    }
  };

  const initAll = () => document.querySelectorAll('[data-rtnu-main-cta]').forEach(initSection);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
